import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Select, Switch, InputNumber, Tooltip } from "antd";
import { SwapOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { LiveDashboardCore } from "../container/modal/LiveDashboardModal/core/LiveDashboardCore";
import IndoorPositioning from "../api/IndoorPositioning";
import "./css/public-live-dashboard.css";

const { Option } = Select;

const PublicLiveDashboard = () => {
  const { machineID } = useParams();
  const history = useHistory();
  const [siblings, setSiblings] = useState([]);
  const [currentMachine, setCurrentMachine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoSwitch, setAutoSwitch] = useState(true);
  const [switchInterval, setSwitchInterval] = useState(20);
  // True while the operator has zoomed or panned the live canvas. Rotating to
  // the next machine out from under someone who is mid-inspection loses the
  // view they just set up, so the timer below stands down until they reset it
  // (LiveCanvas resets the view -- and therefore this flag -- on every machine
  // change, so it can never latch on across sites).
  const [viewTransformed, setViewTransformed] = useState(false);
  const autoSwitchTimerRef = useRef(null);

  useEffect(() => {
    const fetchSiblings = async () => {
      setLoading(true);
      try {
        const response = await IndoorPositioning.GetSiblingMachines(machineID);
        if (response.code === 200 && response.data) {
          setSiblings(response.data.siblings || []);
          setCurrentMachine(response.data.currentMachine);
        }
      } catch (error) {
        console.error("Failed to fetch sibling machines:", error);
      } finally {
        setLoading(false);
      }
    };

    if (machineID) {
      fetchSiblings();
    }
  }, [machineID]);

  useEffect(() => {
    if (autoSwitch && !viewTransformed && siblings.length > 1) {
      const currentIndex = siblings.findIndex((s) => String(s.id) === machineID);
      
      autoSwitchTimerRef.current = setInterval(() => {
        const nextIndex = (currentIndex + 1) % siblings.length;
        const nextMachine = siblings[nextIndex];
        if (nextMachine && String(nextMachine.id) !== machineID) {
          history.push(`/public/live-dashboard/${nextMachine.id}`);
        }
      }, switchInterval * 1000);

      return () => {
        if (autoSwitchTimerRef.current) {
          clearInterval(autoSwitchTimerRef.current);
          autoSwitchTimerRef.current = null;
        }
      };
    } else {
      if (autoSwitchTimerRef.current) {
        clearInterval(autoSwitchTimerRef.current);
        autoSwitchTimerRef.current = null;
      }
    }
  }, [autoSwitch, viewTransformed, switchInterval, machineID, siblings, history]);

  useEffect(() => {
    return () => {
      if (autoSwitchTimerRef.current) {
        clearInterval(autoSwitchTimerRef.current);
      }
    };
  }, []);

  const handleMachineChange = (newMachineID) => {
    if (newMachineID !== machineID) {
      setAutoSwitch(false);
      history.push(`/public/live-dashboard/${newMachineID}`);
    }
  };

  const handleAutoSwitchToggle = (checked) => {
    setAutoSwitch(checked);
  };

  // Stable identity: LiveCanvas keeps this in an effect dependency list.
  const handleViewTransformChange = useCallback(
    (transformed) => setViewTransformed(transformed),
    [],
  );

  const handleIntervalChange = (value) => {
    if (value && value >= 5 && value <= 300) {
      setSwitchInterval(value);
    }
  };

  const displayTitle = currentMachine
    ? `${currentMachine.mobile_name} (${currentMachine.field_name || ""})`
    : `室內定位即時監控 - ${machineID}`;

  return (
    <div className="public-live-dashboard-container">
      <div className="public-dashboard-header">
        <h2>{displayTitle}</h2>
        {siblings.length > 1 && (
          <div className="header-controls">
            <div className="machine-switcher">
              <SwapOutlined className="switcher-icon" />
              <Select
                value={machineID}
                onChange={handleMachineChange}
                style={{ minWidth: 200 }}
                placeholder="選擇設備"
                loading={loading}
                disabled={loading || autoSwitch}
                dropdownMatchSelectWidth={false}
              >
                {siblings.map((sibling) => (
                  <Option key={sibling.id} value={String(sibling.id)}>
                    {sibling.mobile_name}
                    {sibling.isCurrent && " (當前)"}
                  </Option>
                ))}
              </Select>
            </div>
            
            <div className="auto-switch-controls">
              <Tooltip
                title={
                  viewTransformed
                    ? "已縮放/平移畫面，輪播暫停中（重置檢視後恢復）"
                    : "自動輪播切換設備"
                }
              >
                <div className="auto-switch-toggle">
                  <ClockCircleOutlined className="clock-icon" />
                  <Switch
                    checked={autoSwitch}
                    onChange={handleAutoSwitchToggle}
                    size="small"
                  />
                  <span className="toggle-label">自動切換</span>
                </div>
              </Tooltip>
              
              {autoSwitch && (
                <Tooltip title="切換間隔（秒）">
                  <InputNumber
                    min={5}
                    max={300}
                    value={switchInterval}
                    onChange={handleIntervalChange}
                    size="small"
                    style={{ width: 70 }}
                    addonAfter="秒"
                  />
                </Tooltip>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="public-dashboard-content">
        <LiveDashboardCore
          machineID={machineID}
          className="public-variant"
          onViewTransformChange={handleViewTransformChange}
        />
      </div>
    </div>
  );
};

export default PublicLiveDashboard;
